export const CONSENT_COOKIE_NAME = 'pe_cookie_consent';

function getCookieDomain(): string {
  if (location.hostname.endsWith('.passentry.com')) {
    return '.passentry.com';
  }
  return location.hostname;
}

function generateId(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

interface ConsentCookieValue {
  categories: string[];
  consentId: string;
  consentTimestamp: string;
  lastConsentTimestamp: string;
  revision: number;
  data: unknown;
  languageCode: string;
  services: Record<string, string[]>;
  expirationTime: number;
}

function getRawCookie(): string | null {
  const match = document.cookie.match(
    '(^|;)\\s*' + CONSENT_COOKIE_NAME + '\\s*=\\s*([^;]+)'
  );
  return match ? match.pop() || null : null;
}

export function readConsentCookie(): ConsentCookieValue | null {
  try {
    const raw = getRawCookie();
    if (!raw) return null;
    return JSON.parse(decodeURIComponent(raw));
  } catch {
    return null;
  }
}

export function getAcceptedCategories(): string[] {
  return readConsentCookie()?.categories || [];
}

export function hasValidConsent(): boolean {
  const cookie = readConsentCookie();
  return !!(cookie?.consentId && cookie?.categories);
}

export function hasCategory(category: string): boolean {
  return getAcceptedCategories().includes(category);
}

export function writeConsentCookie(categories: string[]): void {
  const now = new Date().toISOString();
  const maxAge = 365 * 24 * 60 * 60;

  const cookieValue: ConsentCookieValue = {
    categories,
    revision: 0,
    data: null,
    consentId: generateId(),
    consentTimestamp: now,
    lastConsentTimestamp: now,
    languageCode: 'en',
    services: {
      essential: [],
      functional: [],
      analytics: [],
      marketing: [],
    },
    expirationTime: Date.now() + maxAge * 1000,
  };

  const domain = getCookieDomain();
  const secure = location.protocol === 'https:' ? '; Secure' : '';

  document.cookie =
    CONSENT_COOKIE_NAME +
    '=' +
    encodeURIComponent(JSON.stringify(cookieValue)) +
    '; domain=' +
    domain +
    '; path=/' +
    '; max-age=' +
    maxAge +
    '; SameSite=Lax' +
    secure;
}
